/**
 * A branding que o loader aceita, e a whitelist que a produz.
 *
 * Mora fora do `index.ts` pelo mesmo motivo que `appearance.ts`: o `index.ts` é
 * um IIFE que se executa ao ser importado, então nada dentro dele é testável
 * sem subir o loader inteiro. `normalizeBranding` é justamente onde os campos
 * morrem em silêncio — foi assim com `appearance`, e depois com `position` —,
 * e é o tipo de função que precisa de teste.
 */

export interface PrechatFieldConfig {
  key: 'phone' | 'email' | 'gender' | 'birthdate';
  required: boolean;
}

export interface BrandingPayload {
  chatbotAvatarUrl?: string | null;
  chatbotName?: string | null;
  primaryColor?: string | null;
  launcherMessage?: string | null;
  launcherTextColor?: 'white' | 'black' | null;
  prechatFields?: PrechatFieldConfig[] | null;
  /**
   * Canto da tela configurado no painel (`chat_widget.position`).
   *
   * Existe porque a posição do snippet (`window.SalevoChatConfig.position`) fica
   * congelada no HTML do cliente no dia em que ele copiou o código: trocar o
   * canto no painel não mudava nada no site, para sempre. O snippet segue como
   * palpite síncrono — evita o launcher nascer de um lado e pular para o outro
   * — e este valor, quando chega, ganha.
   */
  position?: 'left' | 'right' | null;
  /**
   * Aparência configurada no painel (`chat_widget.appearance`), crua.
   *
   * Chega com as chaves em camelCase: o painel grava via `lib/api/http.js`, que
   * aplica `toCamel` no CORPO DE SAÍDA (`http.js:139`), e o backend serve a
   * coluna json sem normalizar. `normalizeAppearance` ainda tolera snake_case
   * por precaução, mas camelCase é o que está gravado — o comentário que dizia
   * o contrário já custou um bug.
   *
   * `null`/ausente indica que o widget ainda não foi configurado. Nesse caso,
   * o loader não revela nada no site do cliente. Quando há configuração,
   * `normalizeAppearance` completa campos novos com seus defaults.
   */
  appearance?: Record<string, unknown> | null;
}

/**
 * O launcher público só deve existir depois que o cliente tiver salvo alguma
 * configuração do widget. `null` e `{}` são ambos "ainda não configurado";
 * normalizá-los para os defaults aqui faria um widget vazio aparecer no site.
 */
export function hasWidgetConfiguration(branding: BrandingPayload): boolean {
  return Boolean(
    branding.appearance &&
      typeof branding.appearance === 'object' &&
      !Array.isArray(branding.appearance) &&
      Object.keys(branding.appearance).length > 0
  );
}

const PRECHAT_KEYS = ['phone', 'email', 'gender', 'birthdate'];

/**
 * Whitelist do que o loader enxerga da resposta de `/api/public/widget/config`.
 *
 * Campo que não é copiado aqui simplesmente não existe para o resto do loader,
 * por mais que o backend o tenha devolvido.
 */
export function normalizeBranding(raw: Record<string, unknown>): BrandingPayload {
  const ltc = raw.launcherTextColor;
  const rawFields = raw.prechatFields;
  const prechatFields = Array.isArray(rawFields)
    ? (rawFields as PrechatFieldConfig[]).filter(
        (f) =>
          f &&
          typeof f === 'object' &&
          PRECHAT_KEYS.includes(f.key) &&
          typeof f.required === 'boolean'
      )
    : null;

  return {
    chatbotAvatarUrl: (raw.chatbotAvatarUrl ?? raw.logoUrl ?? null) as string | null,
    chatbotName: (raw.chatbotName ?? raw.displayName ?? null) as string | null,
    primaryColor: (raw.primaryColor ?? null) as string | null,
    launcherMessage: (raw.launcherMessage ?? null) as string | null,
    launcherTextColor: (ltc === 'white' ? 'white' : 'black') as 'white' | 'black',
    prechatFields: prechatFields && prechatFields.length > 0 ? prechatFields : null,
    // Valor fora do vocabulário vira `null` e o loader mantém o que já tinha,
    // em vez de assumir 'right' e mudar de canto quem configurou 'left'.
    position: normalizePosition(raw.position),
    appearance:
      raw.appearance && typeof raw.appearance === 'object' && !Array.isArray(raw.appearance)
        ? (raw.appearance as Record<string, unknown>)
        : null,
  };
}

/** `'left'`/`'right'`, ou `null` para "não foi dito nada de útil". */
export function normalizePosition(value: unknown): 'left' | 'right' | null {
  if (value === 'left') return 'left';
  if (value === 'right') return 'right';
  return null;
}
